import type { Transition, Variants } from "motion/react"

/**
 * Shared motion tokens for spring-physics interactions.
 *
 * Ported from InteractionKit (MIT, armondschneider/interactionkit) and
 * retokenized against the `motion` block in `design.md`. Companion to
 * `disclosure-motion.ts` (CSS-keyframe disclosure) and
 * `chat-composer-motion.ts` (composer transitions) — reach for this module
 * when the motion is spring-driven or interruptible, and for those when a
 * plain CSS transition is enough.
 *
 * design.md pins user-initiated motion at 150-200ms for color/opacity/small
 * controls and up to 300ms for panels. Springs below are tuned to settle
 * inside those budgets; direct-manipulation gestures (drag release) are the
 * documented exception and may run longer.
 */

/** design.md easing curves, as motion-compatible cubic-bezier tuples. */
export const EASE_ENTER = [0, 0, 0.2, 1] as const
export const EASE_EXIT = [0.4, 0, 1, 1] as const
export const EASE_PHYSICAL = [0.2, 0.8, 0.2, 1] as const
/** Decelerating curve used for blur-morph content swaps. */
export const EASE_MORPH = [0.22, 1, 0.36, 1] as const

/** design.md duration scale, in seconds (motion takes seconds, CSS takes ms). */
export const DURATION_FAST = 0.15
export const DURATION_STANDARD = 0.2
export const DURATION_ENTER = 0.3

/**
 * Press feedback for pointer devices.
 *
 * design.md specifies scale 0.985 for direct-manipulation targets on coarse
 * pointers, which `globals.css` already applies. These deeper values are for
 * discrete controls (buttons, tabs, icon toggles) where the press is a
 * distinct event rather than a drag, and read as inert at 0.985.
 */
export const SPRING_PRESS: Transition = {
  type: "spring",
  stiffness: 500,
  damping: 28,
  mass: 0.5,
}

export const PRESS_SCALE = 0.96
export const PRESS_SCALE_SUBTLE = 0.98
export const PRESS_SCALE_ICON = 0.95

/** Sliding tab/segment indicators. Settles without visible overshoot. */
export const SPRING_INDICATOR: Transition = {
  type: "spring",
  stiffness: 400,
  damping: 30,
}

/** Icon state morphs. Very stiff so rapid toggles stay interruptible. */
export const SPRING_ICON: Transition = {
  type: "spring",
  stiffness: 1000,
  damping: 36,
  mass: 0.25,
}

/** Sheet and drawer entrances driven by drag. */
export const SPRING_SHEET: Transition = {
  type: "spring",
  stiffness: 300,
  damping: 30,
}

/** Loose, weighty spring for card/image stack spreads. */
export const SPRING_STACK: Transition = {
  type: "spring",
  stiffness: 180,
  damping: 24,
  mass: 0.75,
}

/** Shared-element (layoutId) view transitions. */
export const TRANSITION_VIEW: Transition = {
  duration: 0.32,
  ease: EASE_MORPH,
}

/**
 * Blur-morph swap for content that replaces content — tab panels, step
 * changes, filtered lists. Pair with `AnimatePresence mode="wait"`.
 *
 * Do not use for high-frequency controls; the wait-gap reads as lag. Use an
 * interruptible spring there instead.
 */
export const blurMorphVariants: Variants = {
  initial: { opacity: 0, y: 8, filter: "blur(4px)" },
  animate: { opacity: 1, y: 0, filter: "blur(0px)" },
  exit: { opacity: 0, y: -8, filter: "blur(4px)" },
}

export const blurMorphTransition: Transition = {
  duration: 0.25,
  ease: EASE_MORPH,
}

/** Reduced-motion equivalent: opacity only, no transform or blur. */
export const blurMorphReducedVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

export const blurMorphReducedTransition: Transition = {
  duration: DURATION_FAST,
  ease: "linear",
}

/**
 * Picks the correct variant/transition pair for the current motion
 * preference. Callers pass the result of `useReducedMotion()`.
 */
export function morphMotion(prefersReducedMotion: boolean | null) {
  return prefersReducedMotion
    ? { variants: blurMorphReducedVariants, transition: blurMorphReducedTransition }
    : { variants: blurMorphVariants, transition: blurMorphTransition }
}

/**
 * Shallower swap for filtered collections. Incoming results remain legible
 * throughout and never stagger, so changing a filter does not delay data.
 */
export const collectionMorphVariants: Variants = {
  initial: { opacity: 0.4, y: 4, filter: "blur(3px)" },
  animate: { opacity: 1, y: 0, filter: "blur(0px)" },
  exit: { opacity: 0, y: -4, filter: "blur(3px)" },
}

export const collectionMorphTransition: Transition = {
  duration: DURATION_STANDARD,
  ease: EASE_MORPH,
}

export function collectionMotion(prefersReducedMotion: boolean | null) {
  return prefersReducedMotion
    ? {
        variants: blurMorphReducedVariants,
        transition: blurMorphReducedTransition,
      }
    : {
        variants: collectionMorphVariants,
        transition: collectionMorphTransition,
      }
}

/**
 * Overlay entrance for popovers, tooltips, and hover previews: scale + blur
 * lift. Returns `undefined` under reduced motion so the caller can fall back
 * to its CSS animation.
 */
export const overlayLiftVariants: Variants = {
  initial: { opacity: 0, scale: 0.96, y: -4, filter: "blur(2px)" },
  animate: { opacity: 1, scale: 1, y: 0, filter: "blur(0px)" },
  exit: { opacity: 0, scale: 0.96, y: -4, filter: "blur(2px)" },
}

export const overlayLiftTransition: Transition = {
  duration: 0.22,
  ease: [0.16, 1, 0.3, 1],
}

/* -------------------------------------------------------------------------- */
/* Class-name contracts                                                       */
/* -------------------------------------------------------------------------- */

/*
 * Not every control deserves a physics engine. The fragments below give plain
 * CSS surfaces the same press and overlay behavior the spring components have,
 * without shipping `motion` to a dense admin table. They are Tailwind strings,
 * so compose them with `cn()` and let tailwind-merge resolve conflicts.
 */

/**
 * Discrete press feedback for pointer devices.
 *
 * Scoped to `pointer-fine` on purpose: `globals.css` already scales coarse
 * pointers to 0.985, and design.md pins that value for direct manipulation.
 * This is the mouse/trackpad counterpart, where a click is a distinct event
 * and 0.985 reads as inert.
 *
 * The host element must transition `scale`; otherwise the release snaps.
 */
export const PRESSABLE_CLASS = "motion-safe:pointer-fine:active:scale-[0.97]"

/** Softer variant for large surfaces — cards, rows, list items. */
export const PRESSABLE_SUBTLE_CLASS =
  "motion-safe:pointer-fine:active:scale-[0.99]"

/**
 * Cancels every CSS press. Use on controls that drive their own spring
 * (`SpringButton`, `ClickHoldButton`, `CopyButton`) so the CSS scale does not
 * compound with the inline transform.
 *
 * Both pointer types matter. The fine-pointer half cancels
 * {@link PRESSABLE_CLASS}; the coarse-pointer half cancels the 0.985 rule in
 * `globals.css`, which is `:where()`-wrapped and therefore loses to any real
 * class. Without it a touch press multiplied the two — 0.985 x 0.96 = 0.946,
 * deeper than either value intends — because CSS `scale` and motion's inline
 * `transform` compose rather than override.
 *
 * The modifier sets are kept aligned with `PRESSABLE_CLASS` on purpose: matched
 * modifiers let tailwind-merge resolve the pair by order instead of leaving
 * both in the class list to be settled by stylesheet position.
 */
export const PRESSABLE_OFF_CLASS =
  "motion-safe:pointer-fine:active:scale-100 motion-safe:pointer-coarse:active:scale-100"

/**
 * Shared Radix overlay entrance/exit: fade + zoom + a 2 px blur pull.
 *
 * The blur is the depth cue InteractionKit uses on its tooltip — enough to read
 * as a layer arriving, not enough to smear text. Exits run at 150 ms against a
 * 200 ms entrance, per design.md ("keep exits at least as fast as entrances").
 *
 * Under `prefers-reduced-motion` the global rule in `globals.css` sets
 * `animation: none` on `.animate-in`/`.animate-out`, so overlays appear and
 * disappear instantly — no per-component override needed.
 */
export const overlayMotionClass = [
  "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:blur-in-[2px] data-[state=open]:duration-200 data-[state=open]:ease-out",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:blur-out-[2px] data-[state=closed]:duration-150 data-[state=closed]:ease-in",
].join(" ")

/**
 * Directional offset for anchored overlays, paired with
 * {@link overlayMotionClass}. Kept separate because centered dialogs and
 * command palettes have no side to slide from.
 */
export const overlaySideMotionClass =
  "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"

/** Scrim fade for dialog and sheet overlays. Exit is faster than entrance. */
export const overlayScrimMotionClass = [
  "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-200 data-[state=open]:ease-out",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-150 data-[state=closed]:ease-in",
].join(" ")

/**
 * Pop-in for an indicator that mounts on state change — a checkbox tick, a
 * selected-item dot. Stiff and short; the user is confirming their own input,
 * not watching a transition.
 */
export const indicatorPopMotionClass =
  "data-[state=checked]:animate-in data-[state=checked]:zoom-in-50 data-[state=checked]:fade-in-0 data-[state=checked]:duration-150 data-[state=checked]:ease-out"
